import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import {
  addTrialMessage,
  getOrCreateTrialMessages,
  getOrCreateUser,
  setColorMode,
  setRetrieved,
  updateChatSessions
} from "./fireSlice";
import { buildMessageHistory, getActiveSession } from "../utils/Functions";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { auth } from "../utils/firebase";
import { bots } from "../utils/Bots";
import { identifyUser } from "../segment/Events";

console.log('Environment check:', {
  endpoint: process.env.REACT_APP_CHATBASE_ENDPOINT,
  hasApiKey: !!process.env.REACT_APP_CHATBASE_API_KEY,
  botId: process.env.REACT_APP_CHATBASE_CHATBOT_ID_ASK_MOTHER_NATURE
});

const initialState = {
  user: null,
  bot: bots.askMN,
  chatSessions: [], // set of chat logs 12/3/23 chrisdobbie
  activeSession: null,
  trialMessages: [],
  inputMessage: "",
  isLoading: false,
  loadingText: bots.askMN.loadingText,
  examplePrompts: bots.askMN.examplePrompts,
  drawerOpen: false,
  feedbackDialogState: { open: false, positive: null },
  botsMenuOpen: false,
  selectedMessageIndex: null,
  darkMode: false
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    setBot(state, action) {
      state.bot = action.payload;

      const sessionIndex = state.chatSessions.findIndex(
        (session) => session.id === state.activeSession
      );

      // update bot for new sessions
      if (
        sessionIndex !== -1 &&
        state.chatSessions[sessionIndex].messages.length === 0
      ) {
        state.chatSessions[sessionIndex] = {
          ...state.chatSessions[sessionIndex],
          bot: action.payload.id,
          lastActive: Date.now()
        };
      }

      state.examplePrompts = state.bot.examplePrompts;
    },
    setChatSessions(state, action) {
      state.chatSessions = action.payload;
    },
    setActiveSession(state, action) {
      state.activeSession = action.payload;
    },
    setTrialMessages(state, action) {
      state.trialMessages = action.payload;
    },
    setInputMessage(state, action) {
      state.inputMessage = action.payload;
    },
    setIsLoading(state, action) {
      state.isLoading = action.payload;
    },
    setLoadingText(state, action) {
      state.loadingText = action.payload;
    },
    setDrawerOpen(state, action) {
      state.drawerOpen = action.payload;
    },
    setFeedbackDialogState(state, action) {
      state.feedbackDialogState = action.payload;
    },
    setBotsMenuOpen(state, action) {
      state.botsMenuOpen = action.payload;
    },
    setSelectedMessageIndex(state, action) {
      state.selectedMessageIndex = action.payload;
    },
    setDarkMode(state, action) {
      state.darkMode = action.payload;
    },
    addChatMessage(state, action) {
      const sessionIndex = state.chatSessions.findIndex(
        (session) => session.id === state.activeSession
      );

      if (sessionIndex === -1) {
        console.error("Active session not found in chatSessions");
        return;
      }

      const updatedMessages = [
        ...state.chatSessions[sessionIndex].messages,
        action.payload
      ];

      const updatedSession = {
        ...state.chatSessions[sessionIndex],
        messages: updatedMessages
      };

      state.chatSessions = [
        ...state.chatSessions.slice(0, sessionIndex),
        updatedSession,
        ...state.chatSessions.slice(sessionIndex + 1)
      ];
    },
    streamChatMessageText(state, action) {
      const sessionIndex = state.chatSessions.findIndex(
        (session) => session.id === action.payload.id
      );

      if (sessionIndex === -1) {
        console.error("Active session not found in chatSessions");
        return;
      }

      const updatedMessages = [...state.chatSessions[sessionIndex].messages];

      updatedMessages[updatedMessages.length - 1] = {
        ...updatedMessages[updatedMessages.length - 1],
        message: action.payload.message
      };

      const updatedSession = {
        ...state.chatSessions[sessionIndex],
        messages: updatedMessages,
        lastActive: Date.now()
      };

      state.chatSessions = [
        ...state.chatSessions.slice(0, sessionIndex),
        updatedSession,
        ...state.chatSessions.slice(sessionIndex + 1)
      ];
    },
    handleNewChat(state) {
      // prevent adding multiple new chats to the history
      const activeSession = getActiveSession(
        state.chatSessions,
        state.activeSession
      );
      if (
        activeSession.messages.length > 1 ||
        state.chatSessions.length === 0
      ) {
        const id = Date.now();
        const newSession = {
          id: id,
          lastActive: id,
          bot: state.bot.id,
          messages: [],
          snippet: "New Chat"
        };
        state.activeSession = id;
        state.chatSessions = [newSession, ...state.chatSessions];
        state.examplePrompts = state.bot.examplePrompts;
        state.isLoading = false; // stop loading in the new chat
      }
    }
  }
});

export const renameSession = createAsyncThunk(
  "chat/renameSession",
  async ({ id, newName }, { dispatch, getState }) => {
    const { chatSessions, user } = getState().chat;
    const sessionIndex = chatSessions.findIndex((session) => session.id === id);

    if (sessionIndex === -1) {
      console.error("Session not found for renaming");
      return;
    }

    const updatedSessions = [...chatSessions];
    updatedSessions[sessionIndex] = {
      ...updatedSessions[sessionIndex],
      snippet: newName,
      lastActive: Date.now()
    };

    dispatch(setChatSessions(updatedSessions));

    // only update firestore for a logged-in user
    if (user) {
      await dispatch(
        updateChatSessions({ userId: user.uid, chatSessions: updatedSessions })
      );
    }
  }
);

export const handleDeleteChat = createAsyncThunk(
  "chat/handleDeleteChat",
  async (sessionId, { dispatch, getState }) => {
    let { chatSessions, user, activeSession } = getState().chat;

    chatSessions = chatSessions.filter((session) => session.id !== sessionId);

    dispatch(setChatSessions(chatSessions));
    dispatch(
      updateChatSessions({
        userId: user.uid,
        chatSessions: chatSessions
      })
    );

    if (chatSessions.length === 0) {
      dispatch(handleNewChat(getState().chat));
    } else if (sessionId === activeSession) {
      // Load the first chat session if the current one is deleted
      console.log("Deleted active session");
      dispatch(setActiveSession(chatSessions[0].id));
      dispatch(setBot(bots[chatSessions[0].bot]));
    }
  }
);

export const handleSendMessage = createAsyncThunk(
  "chat/handleSendMessage",
  async ({ message, user, free }, { dispatch, getState }) => {
    if (message.trim() === "") return;

    const { chatSessions, trialMessages, activeSession } = getState().chat;

    const activeSessionData = getActiveSession(chatSessions, activeSession);

    const userMessage = {
      message: message,
      time: new Date().toLocaleTimeString(),
      isAI: false,
      index: activeSessionData.messages.length
    };

    dispatch(addChatMessage(userMessage));

    const updatedSession = {
      ...activeSessionData,
      lastActive: Date.now(),
      messages: [...activeSessionData.messages, userMessage]
    };

    const firstMessage =
      updatedSession.messages.filter((msg) => !msg.isAI).length === 1;

    if (firstMessage) {
      updatedSession.snippet =
        message.length > 50 ? `${message.slice(0, 50)}` : message;
    }

    // replace the activeSession with the updated session
    const newSessionsArray = chatSessions.map((session) =>
      session.id === activeSession ? updatedSession : session
    );
    dispatch(setChatSessions(newSessionsArray));

    if (user) {
      dispatch(
        updateChatSessions({ userId: user.uid, chatSessions: newSessionsArray })
      );
      if (firstMessage) dispatch(getSessionSnippet(activeSession));
    } else if (!free) {
      dispatch(addTrialMessage(trialMessages));
    }

    dispatch(setInputMessage(""));

    // Trigger AI response
    await dispatch(handleSendToAI());
  }
);

export const handleSendToAI = createAsyncThunk(
  "chat/handleSendToAI",
  async (_, { dispatch, getState }) => {
    const { chatSessions, bot, activeSession } = getState().chat;

    console.log('Sending AI request:', {
      botId: bot.chatbaseId,
      sessionId: activeSession,
      endpoint: process.env.REACT_APP_CHATBASE_ENDPOINT
    });

    dispatch(setLoadingText(bot.loadingText));
    dispatch(setIsLoading(true));

    const sessionMessages = getActiveSession(
      chatSessions,
      activeSession
    ).messages;

    try {
      const response = await fetch(process.env.REACT_APP_CHATBASE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_CHATBASE_API_KEY}`
        },
        body: JSON.stringify({
          messages: buildMessageHistory(sessionMessages),
          chatbotId: bot.chatbaseId,
          stream: true,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('AI Response error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = response.body;

      if (!data) {
        console.error("No AI response body");
      }

      // add a blank AI message for streaming
      const aiMessageBlank = {
        message: "",
        time: new Date().toLocaleTimeString(),
        isAI: true,
        index: sessionMessages.length
      };
      dispatch(addChatMessage(aiMessageBlank));

      const reader = data.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let fullMessage = "";

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        const chunkValue = decoder.decode(value);
        fullMessage += chunkValue;
        dispatch(
          streamChatMessageText({ message: fullMessage, id: activeSession })
        );
      }

      // update firebase
      const { chatSessions, user } = getState().chat;
      if (user) {
        dispatch(
          updateChatSessions({ userId: user.uid, chatSessions: chatSessions })
        );
      }
    } catch (error) {
      console.error("Error fetching AI response:", error);
      throw error;
    } finally {
      dispatch(setIsLoading(false));
    }
  }
);

export const handleRegenerateMessage = createAsyncThunk(
  "chat/handleRegenerateMessage",
  async (index, { dispatch, getState }) => {
    const { chatSessions, activeSession } = getState().chat;

    const sessionIndex = chatSessions.findIndex(
      (session) => session.id === activeSession
    );

    if (sessionIndex === -1) {
      console.error("Active session could not be found in chatSessions");
      return;
    }

    // hide regenerated AI message
    const updatedMessages = chatSessions[sessionIndex].messages
      .map((message) =>
        message.index === index ? { ...message, hidden: true } : message
      )
      .filter((message) => message.index <= index);

    const regenerateRequestMessage = {
      message:
        "Generate an alternate response to the previous message. Just generate the alternate response as if it's the way you responded the first time (do not acknowledge you have been asked for an alternate response)",
      time: new Date().toLocaleTimeString(),
      isAI: false,
      hidden: true,
      index: updatedMessages.length
    };

    updatedMessages.push(regenerateRequestMessage);

    const updatedSession = {
      ...chatSessions[sessionIndex],
      messages: updatedMessages,
      lastActive: Date.now()
    };

    const newSessionsArray = [
      ...chatSessions.slice(0, sessionIndex),
      updatedSession,
      ...chatSessions.slice(sessionIndex + 1)
    ];

    dispatch(setChatSessions(newSessionsArray));

    await dispatch(handleSendToAI());
  }
);

export const handleResubmitMessage = createAsyncThunk(
  "chat/handleResubmitMessage",
  async (editedMessage, { dispatch, getState }) => {
    const { chatSessions, activeSession, user } = getState().chat;

    const sessionIndex = chatSessions.findIndex(
      (session) => session.id === activeSession
    );

    if (sessionIndex === -1) {
      console.error("Active session not found in chatSessions");
      return;
    }

    // Get the active session and map to update the edited message
    const updatedMessages = chatSessions[sessionIndex].messages
      .map((message) => {
        return message.index === editedMessage.index ? editedMessage : message;
      })
      .filter((message) => message.index <= editedMessage.index);

    const updatedSession = {
      ...chatSessions[sessionIndex],
      messages: updatedMessages,
      lastActive: Date.now()
    };

    const newSessionsArray = [
      ...chatSessions.slice(0, sessionIndex),
      updatedSession,
      ...chatSessions.slice(sessionIndex + 1)
    ];

    dispatch(setChatSessions(newSessionsArray));

    await dispatch(handleSendToAI());

    // update snippet if first message was edited
    const firstMessage =
      updatedSession.messages.filter((msg) => !msg.isAI).length === 1;

    if (user && firstMessage) {
      dispatch(getSessionSnippet(activeSession));
    }
  }
);

export const getSessionSnippet = createAsyncThunk(
  "chat/getSessionSnippet",
  async (sessionId, { dispatch, getState }) => {
    const { chatSessions, bot } = getState().chat;
    const activeSession = getActiveSession(chatSessions, sessionId);

    const messages = [
      {
        role: "user",
        content:
          "My next message is the start of a new chat session. I need a concise and descriptive title for the new chat session. The title should encapsulate and summarize my intentions for sending the message; it will serve as the display title for this chat session. The title should be worded such that I can easily find and remember it in the future when I see it in the list of chat sessions. Search results for chat sessions will be based on this title, so the chat session should be easily searchable by its title. Respond with the title only, exactly as it will appear in the list of chat sessions. Do not surround it in quotes"
      },
      ...buildMessageHistory(activeSession.messages)
    ];

    try {
      const response = await fetch(process.env.REACT_APP_CHATBASE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_CHATBASE_API_KEY}`
        },
        body: JSON.stringify({
          messages: messages,
          chatbotId: bot.chatbaseId,
          stream: false
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.text) {
        dispatch(renameSession({ id: sessionId, newName: data.text }));
      }
    } catch (error) {
      console.error("Error generating prompt snippet: ", error);
    }
  }
);

export const setMessageRating = createAsyncThunk(
  "chat/setMessageRating",
  async ({ positive, messageIndex }, { dispatch, getState }) => {
    const { chatSessions, activeSession, user } = getState().chat;

    const updatedSessions = chatSessions.map((session) => {
      if (session.id === activeSession) {
        const updatedMessages = session.messages.map((message) =>
          message.index === messageIndex
            ? {
                ...message,
                feedback: {
                  positive,
                  comment: ""
                }
              }
            : message
        );
        return {
          ...session,
          messages: updatedMessages
        };
      }
      return session;
    });

    dispatch(setChatSessions(updatedSessions));

    if (user) {
      await dispatch(
        updateChatSessions({ userId: user.uid, chatSessions: updatedSessions })
      );
    }
  }
);

export const setMessageComment = createAsyncThunk(
  "chat/setMessageComment",
  async ({ comment, messageIndex }, { dispatch, getState }) => {
    const { chatSessions, activeSession, user } = getState().chat;

    const updatedSessions = chatSessions.map((session) => {
      if (session.id === activeSession) {
        const updatedMessages = session.messages.map((message) =>
          message.index === messageIndex
            ? {
                ...message,
                feedback: {
                  ...message.feedback,
                  comment: comment
                }
              }
            : message
        );
        return {
          ...session,
          messages: updatedMessages
        };
      }
      return session;
    });

    dispatch(setChatSessions(updatedSessions));

    if (user) {
      await dispatch(
        updateChatSessions({ userId: user.uid, chatSessions: updatedSessions })
      );
    }
  }
);

export const handleSignIn = createAsyncThunk(
  "chat/handleSignIn",
  async (_, { dispatch }) => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log("User signed in:", user);

      dispatch(setUser(user));
      dispatch(handleNewChat());
    } catch (error) {
      console.error("Error during sign in:", error.code, error.message);
    }
  }
);

export const handleSignOut = createAsyncThunk(
  "chat/handleSignOut",
  async (_, { dispatch }) => {
    try {
      await auth.signOut();
      console.log("User signed out successfully");

      dispatch(setUser(null));
      dispatch(setRetrieved(false));
      dispatch(setChatSessions([]));
      dispatch(handleNewChat());
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }
);

export const handleAuthStateChanged = createAsyncThunk(
  "chat/handleAuthStateChanged",
  async (_, { dispatch }) => {
    auth.onAuthStateChanged((user) => {
      dispatch(setUser(user));
      if (user) {
        dispatch(getOrCreateUser(user));
        dispatch(handleNewChat());
        identifyUser(user.uid);
      } else {
        dispatch(getOrCreateTrialMessages());
        dispatch(setChatSessions([]));
        dispatch(handleNewChat());
      }
    });
  }
);

export const setUserDarkMode = createAsyncThunk(
  "chat/setUserDarkMode",
  async (darkMode, { dispatch, getState, rejectWithValue }) => {
    try {
      const userId = getState().chat.user?.uid;

      if (!userId) {
        console.error("User ID not found in Redux state.");
        return rejectWithValue("User ID not found.");
      }

      await dispatch(setColorMode({ userId, darkMode }));

      dispatch(setDarkMode(darkMode));
    } catch (error) {
      console.error("Error updating dark mode:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const {
  setUser,
  setBot,
  setChatSessions,
  setActiveSession,
  setTrialMessages,
  setInputMessage,
  setIsLoading,
  setLoadingText,
  setDrawerOpen,
  setFeedbackDialogState,
  setBotsMenuOpen,
  setSelectedMessageIndex,
  setDarkMode,
  handleNewChat,
  addChatMessage,
  streamChatMessageText
} = chatSlice.actions;

export default chatSlice.reducer;
